<script setup lang="ts">
import type { EChartsCoreOption } from 'echarts/core'
import type { RouteLocationRaw } from 'vue-router'
import { getWebHomeTradingVolumeRankings, type GetWebHomeTradingVolumeRankingsApiResponse } from '~/server/home'

type TrendDirection = 'up' | 'down'

type TradingRow = {
  rank: number
  exchange: string
  logo: string
  currencies: string
  volume: string
  liquidity: string
  trend: TrendDirection
  trendSeries: number[]
  alias: string
  traderCode: string
  detailRoute?: RouteLocationRaw
}
const { localePath, t } = useAppI18n()
const matomo = useMatomo()

const TRADING_PAGE_SIZE = 10
const TRADING_SKELETON_ROWS = 10
/** 仅供公共分页组件生成链接；链接点击会被拦截为本地接口翻页。 */
const TRADING_PAGE_ROUTE = '/page/{idx}'

const props = defineProps<{
  data?: Record<string, unknown>
}>()

const tradingPayload = ref<Record<string, unknown>>({})
const currentPage = ref(1)
const isPageLoading = ref(false)
let latestTradingRequestId = 0

const normalizeTradingPayload = (
  payload: GetWebHomeTradingVolumeRankingsApiResponse | null | undefined,
): Record<string, unknown> => {
  if (!payload || typeof payload !== 'object') {
    return {}
  }

  if ('data' in payload && payload.data && typeof payload.data === 'object') {
    return payload.data as Record<string, unknown>
  }

  return payload as Record<string, unknown>
}

watch(
  () => props.data,
  (nextData) => {
    tradingPayload.value = nextData && typeof nextData === 'object' ? { ...nextData } : {}
    const page = tradingPayload.value.page
    currentPage.value = typeof page === 'number' && Number.isFinite(page) && page > 0 ? page : 1
  },
  { immediate: true },
)

/**
 * 统一生成交易所详情页路由，和实勘交易所列表保持一致。
 */
const buildExchangeDetailRoute = (slug?: string): RouteLocationRaw | undefined => {
  if (!slug) {
    return undefined
  }

  return localePath({
    name: 'exchange-detail',
    params: {
      slug,
    },
  } as RouteLocationRaw) as RouteLocationRaw
}

/**
 * 交易量排行只允许使用接口返回的别名作为详情页 slug，缺失时禁用跳转。
 */
const getExchangeDetailAlias = (record: Record<string, unknown>, exchange: Record<string, unknown>) => {
  const alias = typeof record.alias === 'string' && record.alias.trim() ? record.alias : exchange.alias

  return typeof alias === 'string' ? alias.trim() : ''
}

/**
 * 将接口中的交易数值字段统一转成列表可展示文本。
 */
const formatTradingMetricValue = (value: unknown) => {
  if (typeof value === 'string' && value.trim()) {
    return value
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value)
  }

  return '--'
}

const normalizeTradingText = (value: unknown) => {
  if (typeof value === 'string') {
    return value
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value)
  }

  return ''
}

const tradingRows = computed<TradingRow[]>(() => {
  const payload = tradingPayload.value
  const rows = payload?.rows

  if (!Array.isArray(rows) || rows.length === 0) {
    return []
  }

  return rows
    .map((item, index) => {
      if (!item || typeof item !== 'object') {
        return null
      }

      const record = item as Record<string, unknown>
      const exchange = (record.exchange as Record<string, unknown> | undefined) ?? {}
      const trend7d = (record.trend7d as Record<string, unknown> | undefined) ?? {}
      const alias = getExchangeDetailAlias(record, exchange)
      const traderCode = normalizeTradingText(record.traderCode)
      const points = Array.isArray(trend7d.points) ? trend7d.points : []
      const trendSeries = points
        .map((point) => {
          if (!point || typeof point !== 'object') {
            return NaN
          }
          const value = (point as Record<string, unknown>).value
          return typeof value === 'number' ? value : Number(value)
        })
        .filter((value) => Number.isFinite(value))

      const trendType = typeof trend7d.trendType === 'number' ? trend7d.trendType : 0

      return {
        rank: typeof record.rank === 'number' ? record.rank : index + 1,
        exchange: typeof exchange.name === 'string' && exchange.name ? exchange.name : 'Unknown',
        logo: typeof exchange.logo === 'string' ? exchange.logo : '',
        currencies:
          typeof record.supportedCoinsCount === 'number'
            ? String(record.supportedCoinsCount)
            : typeof record.supportedCoinsCount === 'string'
              ? record.supportedCoinsCount
              : '--',
        volume: formatTradingMetricValue(record.tradeVolume24hValue),
        liquidity: formatTradingMetricValue(record.avgLiquidityText),
        trend: trendType === 2 ? 'down' : 'up',
        trendSeries:
          trendSeries.length > 1 ? trendSeries : trendType === 2 ? TREND_SERIES_MAP.down : TREND_SERIES_MAP.up,
        alias,
        traderCode,
        detailRoute: buildExchangeDetailRoute(alias),
      } satisfies TradingRow
    })
    .filter((item): item is TradingRow => item !== null)
})

const pageSize = computed(() => {
  const payloadPageSize = tradingPayload.value.pageSize
  if (typeof payloadPageSize === 'number' && Number.isFinite(payloadPageSize) && payloadPageSize > 0) {
    return payloadPageSize
  }
  return TRADING_PAGE_SIZE
})

const total = computed(() => {
  const payloadTotal = tradingPayload.value.total
  if (typeof payloadTotal === 'number' && Number.isFinite(payloadTotal) && payloadTotal > 0) {
    return payloadTotal
  }
  return tradingRows.value.length
})

const totalPages = computed(() => {
  if (total.value <= 0 || pageSize.value <= 0) {
    return 0
  }
  return Math.ceil(total.value / pageSize.value)
})

watch(totalPages, (pageCount) => {
  if (pageCount <= 0) {
    currentPage.value = 1
    return
  }

  if (currentPage.value > pageCount) {
    currentPage.value = pageCount
  } else if (currentPage.value < 1) {
    currentPage.value = 1
  }
})

const fetchTradingPage = async (page: number) => {
  const safePage = Math.trunc(page)
  const pageCount = totalPages.value

  if (safePage < 1 || (pageCount > 0 && safePage > pageCount) || safePage === currentPage.value) {
    return
  }

  const requestId = ++latestTradingRequestId
  isPageLoading.value = true

  try {
    const response = await getWebHomeTradingVolumeRankings({
      page: safePage,
      pageSize: pageSize.value,
    })

    if (requestId !== latestTradingRequestId) {
      return
    }

    const normalized = normalizeTradingPayload(response)
    const responsePage = normalized.page
    const nextPage =
      typeof responsePage === 'number' && Number.isFinite(responsePage) && responsePage > 0 ? responsePage : safePage

    tradingPayload.value = {
      ...tradingPayload.value,
      ...normalized,
      page: nextPage,
    }
    currentPage.value = nextPage
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('getWebHomeTradingVolumeRankings failed', error)
  } finally {
    if (requestId === latestTradingRequestId) {
      isPageLoading.value = false
    }
  }
}

/** 拦截公共分页组件的链接跳转，改为请求交易量榜单的目标页数据。 */
const handlePaginationClick = (event: MouseEvent) => {
  const anchor = (event.target as HTMLElement).closest('a')

  if (!anchor || typeof window === 'undefined') {
    return
  }

  event.preventDefault()
  event.stopPropagation()

  if (isPageLoading.value) {
    return
  }

  const url = new URL(anchor.href, window.location.origin)
  const matchedPage = url.pathname.match(/\/page\/(\d+)(?:\/)?$/)
  const targetPage = matchedPage ? Number(matchedPage[1]) : 1

  matomo.trackEvent('home_volume_rank', {
    af_type: '切换页',
  })
  void fetchTradingPage(targetPage)
}

const onTradingRowClick = (row: TradingRow) => {
  matomo.trackEvent('home_volume_rank', {
    af_type: '详情',
    af_name: row.exchange,
    af_code: row.traderCode,
  })
}

const rankClassMap: Record<number, string> = {
  1: 'rank-first',
  2: 'rank-second',
  3: 'rank-third',
}

const TREND_SERIES_MAP: Record<TrendDirection, number[]> = {
  up: [9.2, 9.8, 7.3, 10.6, 10.4, 13.3, 12.1, 16.8, 14.2, 11.7, 13.5, 11.9, 13.7, 12.8, 14.1, 13.3],
  down: [14.7, 13.2, 13.7, 12.1, 10.4, 8.5, 9.3, 8.2, 10.6, 9.4, 12.6, 10.7, 11.3, 9.9, 10.8, 10.1],
}
const TREND_COLOR_MAP: Record<TrendDirection, string> = {
  up: '#72D8B0',
  down: '#F17E72',
}
const TREND_INTRO_DURATION_MS = 1000
const TREND_INTRO_TRIGGER_DELAY_MS = 500
const TREND_AREA_MAX_ALPHA = 0.3

const tradingSectionRef = ref<HTMLElement | null>(null)
const trendChartRefs = ref<Array<HTMLElement | null>>([])
const trendCharts: Array<ReturnType<typeof init>> = []
const { init } = useEcharts()

let tradingObserver: IntersectionObserver | null = null
let hasTrendIntroPlayed = false
let hasTrendIntroScheduled = false
let trendIntroRafId = 0
let trendIntroTimeoutId: ReturnType<typeof setTimeout> | null = null

const setTrendChartRef = (el: Element | null, index: number) => {
  trendChartRefs.value[index] = el instanceof HTMLElement ? el : null
}

const clamp = (value: number, min: number, max: number) => {
  return Math.min(Math.max(value, min), max)
}

const toHexAlpha = (alpha: number) => {
  return Math.round(clamp(alpha, 0, 1) * 255)
    .toString(16)
    .padStart(2, '0')
    .toUpperCase()
}

const buildAnimatedTrendData = (trendData: number[], progress: number) => {
  const clampedProgress = clamp(progress, 0, 1)
  const dataLength = trendData.length

  if (!dataLength) {
    return []
  }

  if (clampedProgress <= 0) {
    return [trendData[0], ...Array.from<number | null>({ length: dataLength - 1 }).fill(null)]
  }

  if (clampedProgress >= 1) {
    return [...trendData]
  }

  const maxIndex = dataLength - 1
  const position = clampedProgress * maxIndex
  const leftIndex = Math.floor(position)
  const ratio = position - leftIndex
  const animatedData = Array.from<number | null>({ length: dataLength }).fill(null)

  for (let index = 0; index < leftIndex; index += 1) {
    animatedData[index] = trendData[index]
  }

  if (leftIndex >= maxIndex) {
    animatedData[maxIndex] = trendData[maxIndex]
    return animatedData
  }

  const leftValue = trendData[leftIndex]
  const rightValue = trendData[leftIndex + 1]
  animatedData[leftIndex] = leftValue + (rightValue - leftValue) * ratio

  return animatedData
}

const getTrendChartOption = (trend: TrendDirection, trendSeries: number[], progress = 1): EChartsCoreOption => {
  const trendData = trendSeries.length > 0 ? trendSeries : TREND_SERIES_MAP[trend]
  const minValue = Math.min(...trendData)
  const maxValue = Math.max(...trendData)
  const yPadding = (maxValue - minValue) * 0.32
  const color = TREND_COLOR_MAP[trend]
  const areaAlpha = toHexAlpha(TREND_AREA_MAX_ALPHA * clamp(progress, 0, 1))
  const seriesData = buildAnimatedTrendData(trendData, progress)

  return {
    animation: false,
    grid: {
      top: 1,
      right: 0,
      bottom: 1,
      left: 0,
      containLabel: false,
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: trendData.map((_, index) => index),
      show: false,
    },
    yAxis: {
      type: 'value',
      min: minValue - yPadding,
      max: maxValue + yPadding,
      show: false,
      scale: true,
    },
    series: [
      {
        type: 'line',
        data: seriesData,
        animation: false,
        showSymbol: false,
        connectNulls: false,
        smooth: false,
        symbol: 'none',
        lineStyle: {
          width: 1.2,
          color,
          cap: 'round',
          join: 'round',
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: `${color}${areaAlpha}` },
              { offset: 1, color: `${color}00` },
            ],
          },
        },
      },
    ],
    tooltip: {
      show: false,
    },
    silent: true,
  }
}

const renderTrendCharts = (progress = 1) => {
  tradingRows.value.forEach((row, index) => {
    const dom = trendChartRefs.value[index]

    if (!dom) {
      return
    }

    const existedChart = trendCharts[index]
    if (existedChart && existedChart.getDom() !== dom) {
      existedChart.dispose()
      trendCharts[index] = null
    }

    if (!trendCharts[index]) {
      trendCharts[index] = init(dom, { lazy: true })
    }

    const chart = trendCharts[index]

    if (!chart) {
      return
    }

    chart.setOption(getTrendChartOption(row.trend, row.trendSeries, progress), {
      notMerge: true,
      lazyUpdate: true,
    })
  })
}

const disposeAllTrendCharts = () => {
  trendCharts.forEach((chart, index) => {
    chart?.dispose()
    trendCharts[index] = null
  })
  trendCharts.length = 0
}

const stopTrendIntroAnimation = () => {
  if (!trendIntroRafId) {
    return
  }

  window.cancelAnimationFrame(trendIntroRafId)
  trendIntroRafId = 0
}

const clearTrendIntroSchedule = () => {
  if (!trendIntroTimeoutId) {
    return
  }

  clearTimeout(trendIntroTimeoutId)
  trendIntroTimeoutId = null
  hasTrendIntroScheduled = false
}

const playTrendIntroAnimation = async (resetCharts = false) => {
  if (!import.meta.client) {
    return
  }

  await nextTick()
  stopTrendIntroAnimation()

  if (resetCharts) {
    disposeAllTrendCharts()
  }

  renderTrendCharts(0)
  onResize()

  const startAt = performance.now()

  const step = (timestamp: number) => {
    const progress = clamp((timestamp - startAt) / TREND_INTRO_DURATION_MS, 0, 1)
    renderTrendCharts(progress)
    onResize()

    if (progress >= 1) {
      trendIntroRafId = 0
      return
    }

    trendIntroRafId = window.requestAnimationFrame(step)
  }

  trendIntroRafId = window.requestAnimationFrame(step)
}

watch(tradingRows, async (rows) => {
  if (!import.meta.client) {
    return
  }

  await nextTick()

  for (let index = rows.length; index < trendCharts.length; index += 1) {
    trendCharts[index]?.dispose()
    trendCharts[index] = null
  }

  trendChartRefs.value.length = rows.length
  trendCharts.length = rows.length
})

watch(currentPage, () => {
  if (!hasTrendIntroPlayed) {
    return
  }

  void playTrendIntroAnimation(true)
})

const onResize = () => {
  trendCharts.forEach((chart, index) => {
    const dom = trendChartRefs.value[index]

    if (!chart || !dom) {
      return
    }

    chart.resize({
      width: dom.clientWidth,
      height: dom.clientHeight,
    })
  })
}

const startTrendIntro = () => {
  if (hasTrendIntroPlayed) {
    return
  }

  clearTrendIntroSchedule()
  hasTrendIntroPlayed = true
  void playTrendIntroAnimation(false)
  tradingObserver?.disconnect()
  tradingObserver = null
}

const scheduleTrendIntro = (delay = TREND_INTRO_TRIGGER_DELAY_MS) => {
  if (hasTrendIntroPlayed || hasTrendIntroScheduled) {
    return
  }

  hasTrendIntroScheduled = true
  trendIntroTimeoutId = window.setTimeout(() => {
    trendIntroTimeoutId = null
    hasTrendIntroScheduled = false
    window.requestAnimationFrame(() => {
      startTrendIntro()
    })
  }, delay)
}

onMounted(() => {
  window.addEventListener('resize', onResize)

  nextTick(() => {
    if (!tradingSectionRef.value) {
      scheduleTrendIntro()
      return
    }

    if (!('IntersectionObserver' in window)) {
      scheduleTrendIntro()
      return
    }

    tradingObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries

        if (!entry?.isIntersecting) {
          return
        }

        scheduleTrendIntro()
      },
      {
        threshold: 0.35,
      },
    )

    tradingObserver.observe(tradingSectionRef.value)
  })
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
  tradingObserver?.disconnect()
  tradingObserver = null
  clearTrendIntroSchedule()
  stopTrendIntroAnimation()
  disposeAllTrendCharts()
})
</script>

<template>
  <section v-if="tradingRows.length > 0" ref="tradingSectionRef" class="trading" aria-label="Trading Volume Rankings">
    <div class="trading-header">
      <h2 class="trading-title">{{ t('82487') }}</h2>
    </div>

    <div class="trading-table-shell p-[12px_16px_20px_32px] rtl:px-[12px_32px_20px_16px]">
      <div class="trading-table-scroll">
        <table class="trading-table" :aria-busy="isPageLoading">
          <colgroup>
            <col class="col-rank" />
            <col class="col-exchange" />
            <col class="col-currencies" />
            <col class="col-volume" />
            <col class="col-liquidity" />
            <col class="col-trend" />
          </colgroup>
          <thead>
            <tr>
              <th class="align-center">#</th>
              <th class="align-left">{{ t('82523') }}</th>
              <th class="align-right">{{ t('82503') }}</th>
              <th class="align-right">{{ t('82506') }}</th>
              <th class="align-right">{{ t('82504') }}</th>
              <th class="align-right !pr-[16px] rtl:!pr-[0] rtl:!pl-[16px]">{{ t('82505', ['7']) }}</th>
            </tr>
          </thead>
          <tbody
            v-if="isPageLoading"
            class="trading-skeleton"
            role="status"
            aria-live="polite"
            aria-label="Loading trading volume rankings"
          >
            <tr v-for="index in TRADING_SKELETON_ROWS" :key="`trading-skeleton-${index}`" aria-hidden="true">
              <td class="align-center">
                <span class="trading-skeleton-rank">{{ index }}</span>
              </td>
              <td class="align-left">
                <div class="trading-skeleton-exchange">
                  <span class="trading-skeleton-logo"></span>
                  <span class="trading-skeleton-item trading-skeleton-name"></span>
                </div>
              </td>
              <td class="align-right">
                <span class="trading-skeleton-item trading-skeleton-metric"></span>
              </td>
              <td class="align-right">
                <span class="trading-skeleton-item trading-skeleton-metric"></span>
              </td>
              <td class="align-right">
                <span class="trading-skeleton-item trading-skeleton-metric"></span>
              </td>
              <td class="align-right !pr-[16px] rtl:!pr-[0] rtl:!pl-[16px]">
                <span class="trading-skeleton-item trading-skeleton-trend"></span>
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr v-for="(row, index) in tradingRows" :key="`${currentPage}-${row.rank}-${row.exchange}`">
              <td class="align-center">
                <NuxtLink
                  v-if="row.detailRoute"
                  :to="row.detailRoute"
                  class="trading-cell-link cursor-pointer hover:text-[#ff5433]"
                  target="_blank"
                  rel="noopener noreferrer"
                  @click="onTradingRowClick(row)"
                >
                  <span class="rank-text" :class="rankClassMap[row.rank]">{{ row.rank }}</span>
                </NuxtLink>
                <span v-else class="trading-cell-link">
                  <span class="rank-text" :class="rankClassMap[row.rank]">{{ row.rank }}</span>
                </span>
              </td>
              <td class="align-left">
                <NuxtLink
                  v-if="row.detailRoute"
                  :to="row.detailRoute"
                  class="trading-cell-link exchange-cell cursor-pointer hover:text-[#ff5433]"
                  target="_blank"
                  rel="noopener noreferrer"
                  @click="onTradingRowClick(row)"
                >
                  <CommonImage
                    :src="row.logo"
                    :alt="`${row.exchange} logo`"
                    class="exchange-logo rounded-[4px]"
                    loading="lazy"
                    object-fit="cover"
                  />
                  <span class="exchange-name">{{ row.exchange }}</span>
                </NuxtLink>
                <span v-else class="trading-cell-link exchange-cell">
                  <CommonImage
                    :src="row.logo"
                    :alt="`${row.exchange} logo`"
                    class="exchange-logo rounded-[4px]"
                    loading="lazy"
                    object-fit="cover"
                  />
                  <span class="exchange-name">{{ row.exchange }}</span>
                </span>
              </td>
              <td class="align-right">
                <NuxtLink
                  v-if="row.detailRoute"
                  :to="row.detailRoute"
                  class="trading-cell-link cursor-pointer hover:text-[#ff5433]"
                  target="_blank"
                  rel="noopener noreferrer"
                  @click="onTradingRowClick(row)"
                >
                  {{ row.currencies }}
                </NuxtLink>
                <span v-else class="trading-cell-link">{{ row.currencies }}</span>
              </td>
              <td class="align-right">
                <NuxtLink
                  v-if="row.detailRoute"
                  :to="row.detailRoute"
                  class="trading-cell-link cursor-pointer hover:text-[#ff5433]"
                  target="_blank"
                  rel="noopener noreferrer"
                  @click="onTradingRowClick(row)"
                >
                  {{ row.volume }}
                </NuxtLink>
                <span v-else class="trading-cell-link">{{ row.volume }}</span>
              </td>
              <td class="align-right">
                <NuxtLink
                  v-if="row.detailRoute"
                  :to="row.detailRoute"
                  class="trading-cell-link cursor-pointer hover:text-[#ff5433]"
                  target="_blank"
                  rel="noopener noreferrer"
                  @click="onTradingRowClick(row)"
                >
                  {{ row.liquidity }}
                </NuxtLink>
                <span v-else class="trading-cell-link">{{ row.liquidity }}</span>
              </td>
              <td class="align-right !pr-[16px] rtl:!pr-[0] rtl:!pl-[16px]">
                <NuxtLink
                  v-if="row.detailRoute"
                  :to="row.detailRoute"
                  class="trading-cell-link cursor-pointer hover:text-[#ff5433]"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="View exchange detail"
                  @click="onTradingRowClick(row)"
                >
                  <div :ref="(el) => setTrendChartRef(el, index)" class="trend-chart" aria-hidden="true"></div>
                </NuxtLink>
                <div v-else class="trading-cell-link">
                  <div :ref="(el) => setTrendChartRef(el, index)" class="trend-chart" aria-hidden="true"></div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <nav
        v-if="totalPages > 1"
        class="trading-pagination"
        aria-label="Trading ranking pagination"
        @click.capture="handlePaginationClick"
      >
        <CommonPagination
          :current-page="currentPage"
          :total="total"
          :page-size="pageSize"
          :page-route="TRADING_PAGE_ROUTE"
          native-links
        />
      </nav>
    </div>
  </section>
</template>

<style scoped lang="less">
.trading {
  position: relative;
  z-index: 0;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 0 100px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  background: #f8f8f8;
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 50%;
    bottom: 0;
    left: 50%;
    width: 100vw;
    transform: translateX(-50%);
    background: #f8f8f8;
    z-index: -1;
  }

  .trading-header {
    height: 32px;
    display: flex;
    align-items: center;
  }

  .trading-title {
    margin: 0;
    color: #292a2e;
    font-size: 24px;
    font-weight: 600;
    line-height: 32px;
  }

  .trading-table-shell {
    border-radius: 8px;
    background: #fff;
  }

  .trading-table-scroll {
    width: 100%;
  }

  .trading-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    color: #292a2e;

    .col-rank {
      width: 54px;
    }

    .col-exchange {
      width: auto;
    }

    .col-currencies {
      width: auto;
    }

    .col-volume {
      width: 248px;
    }

    .col-liquidity {
      width: 248px;
    }

    .col-trend {
      width: 228px;
    }

    thead tr {
      border-bottom: 0.5px solid rgba(11, 18, 40, 0.14);
    }

    th {
      padding: 20px 0;
      color: #8c8f97;
      font-size: 14px;
      font-weight: 400;
      line-height: 22px;
    }

    td {
      padding: 0;
      font-size: 16px;
      font-weight: 500;
      line-height: 24px;
      vertical-align: middle;
    }

    tbody tr {
      cursor: pointer;
      transition: background-color 0.2s ease;

      &:hover td {
        background: #f8f8f8;
      }

      &:hover td:first-child {
        border-start-start-radius: 8px;
        border-end-start-radius: 8px;
      }

      &:hover td:last-child {
        border-start-end-radius: 8px;
        border-end-end-radius: 8px;
      }
    }

    .trading-cell-link {
      display: block;
      width: 100%;
      box-sizing: border-box;
      padding: 20px 0;
      color: inherit;
      text-decoration: none;
    }

    .align-left {
      text-align: left;
    }

    .align-right {
      text-align: right;
    }

    .align-center {
      text-align: center;
    }

    .exchange-cell {
      display: flex;
      align-items: center;
      gap: 12px;
      min-width: 0;
    }

    .exchange-logo {
      width: 32px;
      height: 32px;
      border-radius: 4px;
      object-fit: cover;
      flex-shrink: 0;
    }

    .exchange-name {
      display: block;
      min-width: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      color: #292a2e;
      font-size: 16px;
      font-weight: 500;
      line-height: 24px;
    }

    .rank-text {
      color: #b7b9be;
      font-size: 16px;
      font-weight: 400;
      line-height: 24px;

      &.rank-first {
        color: #ff5433;
      }

      &.rank-second {
        color: #ff7a33;
      }

      &.rank-third {
        color: #fbb22b;
      }
    }

    .trend-chart {
      width: 78px;
      height: 29px;
      display: inline-block;
      vertical-align: middle;

      :deep(canvas) {
        display: block;
      }
    }

    .trading-skeleton {
      tr {
        cursor: default;
      }

      tr:hover td {
        background: transparent;
      }

      .trading-skeleton-rank {
        display: inline-block;
        padding: 24px 0;
        color: #7d818a;
        font-size: 16px;
        font-weight: 400;
        line-height: 24px;
      }

      .trading-skeleton-exchange {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 20px 0;
      }

      .trading-skeleton-logo {
        width: 32px;
        height: 32px;
        flex-shrink: 0;
        border-radius: 4px;
        background: #f0f1f2;
      }

      .trading-skeleton-item {
        display: inline-block;
        height: 20px;
        border-radius: 6px;
        background: #f0f1f2;
        vertical-align: middle;
      }

      .trading-skeleton-name {
        width: 80px;
      }

      .trading-skeleton-metric {
        width: 140px;
      }

      .trading-skeleton-trend {
        width: 86px;
        height: 22px;
      }
    }
  }

  .trading-pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 20px;
  }
}

@media screen and (max-width: 1200px) and (min-width: 801px) {
  .trading {
    width: calc(100% - 48px);

    .trading-table {
      table-layout: fixed;

      .col-rank {
        width: 4.7535%;
      }

      .col-exchange {
        width: 18.1338%;
      }

      .col-currencies {
        width: 13.3803%;
      }

      .col-volume {
        width: 21.831%;
      }

      .col-liquidity {
        width: 21.831%;
      }

      .col-trend {
        width: 20.0704%;
      }
    }
  }
}

@media screen and (max-width: 800px) {
  .trading {
    width: 800px;
    min-width: 800px;
    max-width: none;
  }
}
</style>
